<!-- =========================================================================================
  File Name: OrdersReport.vue
  Description: Monthly orders report (Excel download)
  ----------------------------------------------------------------------------------------
  Item Name: FastWay - Stock transactions' list for management
  Author: Oscar Ramírez O.
  Author URL: https://acentoNET.com
========================================================================================== -->

<template>

  <div>
    <div class="vx-col w-full mb-base">
      <vx-card title="Reporte de órdenes por mes" class="m-1">
        <div class="flex justify-start items-end">
          <div class="mr-2" style="min-width: 200px;">
            <vs-select label="Mes" v-model="month" class="w-full">
              <vs-select-item
                v-for="(item, index) in months"
                :key="index"
                :value="index"
                :text="item"/>
            </vs-select>
          </div>
          <div class="mr-2" style="min-width: 120px;">
            <vs-select label="Año" v-model="year" class="w-full">
              <vs-select-item
                v-for="(item, index) in years"
                :key="index"
                :value="item"
                :text="item.toString()"/>
            </vs-select>
          </div>
          <div>
            <vs-button color="success" type="filled" icon-pack="feather" icon="icon-download" class="ml-4" style="border-radius: 4px;" @click="generate">Descargar</vs-button>
          </div>
        </div>
      </vx-card>
    </div>
  </div>

</template>

<script>
  import XLSX from 'xlsx-js-style';
  import {saveAs} from 'file-saver';
  import {db} from '@/firebaseConfig';

  const DATE_FORMAT = 'dd/mm/yyyy\\ hh:mm:ss';
  const MONEY_FORMAT = '_-* #,##0.00_-;\\-* #,##0.00_-;_-* "-"??_-;_-@_-';
  const QUANTITY_FORMAT = '_-* #,##0_-;\\-* #,##0_-;_-* "-"??_-;_-@_-';
  const HEADER_STYLE = {
    font: {bold: true},
    border: {bottom: {style: 'thin', color: {rgb: '000000'}}}
  };
  const TOTAL_STYLE = {
    font: {bold: true},
    border: {top: {style: 'thin', color: {rgb: '000000'}}}
  };
  const TOTAL_LABEL_STYLE = {
    font: {bold: true},
    border: {top: {style: 'thin', color: {rgb: '000000'}}},
    alignment: {horizontal: 'right'}
  };
  const MONTH_NAMES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

  export default {
    data() {
      const now = new Date();
      const years = [];
      for (let y = 2020; y <= now.getFullYear(); y++) {
        years.push(y);
      }
      return {
        month: now.getMonth() === 0 ? 11 : now.getMonth() - 1,
        year: now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear(),
        months: MONTH_NAMES.map(m => m.charAt(0).toUpperCase() + m.slice(1)),
        years: years
      }
    },
    methods: {
      excelSerial(date) {
        return ((date.getTime() - (date.getTimezoneOffset() * 60000)) / 86400000) + 25569;
      },
      generate() {
        const start = new Date(this.year, this.month, 1, 0, 0, 0);
        const end = new Date(this.year, this.month + 1, 1, 0, 0, 0);
        this.$vs.loading({
          text: 'Generando reporte de órdenes',
          type: 'point'
        });
        const self = this;
        db.collection('orders')
          .where('date', '>=', start)
          .where('date', '<', end)
          .get()
          .then(querySnapshot => {
            self.$vs.loading.close();
            const orders = querySnapshot.docs.map(doc => doc.data());
            if (orders.length === 0) {
              self.$vs.notify({
                title: 'Sin órdenes',
                text: 'No se encontraron órdenes para el período seleccionado',
                color: 'warning'
              });
              return;
            }
            orders.sort((a, b) => a.id - b.id);
            self.download(orders);
          })
          .catch((error) => {
            self.$vs.loading.close();
            self.$vs.notify({
              title: 'Error al generar reporte de órdenes',
              text: error.message,
              color: 'danger'
            });
          });
      },
      download(orders) {
        let maxSlots = 5;
        orders.forEach(order => {
          const count = order.products ? order.products.length : 0;
          if (count > maxSlots) {
            maxSlots = count;
          }
        });
        const header = ['id', 'userName', 'email', 'phone', 'address', 'cityName', 'stateName', 'date', 'orderTotal', 'couponCode'];
        for (let i = 1; i <= maxSlots; i++) {
          header.push(`quantity${i}`, `product${i}`, `price${i}`);
        }
        const aoa = [header];
        let total = 0;
        orders.forEach(order => {
          const products = order.products ? order.products : [];
          const address = order.address ? order.address : {};
          total += order.orderTotal ? order.orderTotal : 0;
          const row = [
            order.id,
            order.userName ? order.userName : '',
            order.user ? order.user : '',
            order.phone ? order.phone : '',
            address.address ? address.address : '',
            address.cityName ? address.cityName : '',
            address.stateName ? address.stateName : '',
            this.excelSerial(order.date.toDate()),
            order.orderTotal ? order.orderTotal : 0,
            order.couponCode ? order.couponCode : ''
          ];
          for (let i = 0; i < maxSlots; i++) {
            if (i < products.length) {
              row.push(products[i].quantity ? products[i].quantity : 0, products[i].description ? products[i].description : '', products[i].price ? products[i].price : 0);
            } else {
              row.push(0, '', 0);
            }
          }
          aoa.push(row);
        });
        const totalRow = new Array(header.length).fill('');
        totalRow[7] = 'Total:';
        totalRow[8] = Math.round(total * 100) / 100;
        aoa.push(totalRow);

        const ws = XLSX.utils.aoa_to_sheet(aoa);
        for (let c = 0; c < header.length; c++) {
          ws[XLSX.utils.encode_cell({c: c, r: 0})].s = HEADER_STYLE;
        }
        for (let r = 1; r < aoa.length; r++) {
          ws[XLSX.utils.encode_cell({c: 7, r: r})].z = DATE_FORMAT;
          ws[XLSX.utils.encode_cell({c: 8, r: r})].z = MONEY_FORMAT;
          if (r < aoa.length - 1) {
            for (let i = 0; i < maxSlots; i++) {
              ws[XLSX.utils.encode_cell({c: 10 + (i * 3), r: r})].z = QUANTITY_FORMAT;
              ws[XLSX.utils.encode_cell({c: 12 + (i * 3), r: r})].z = MONEY_FORMAT;
            }
          }
        }
        const lastRow = aoa.length - 1;
        ws[XLSX.utils.encode_cell({c: 7, r: lastRow})].s = TOTAL_LABEL_STYLE;
        ws[XLSX.utils.encode_cell({c: 8, r: lastRow})].s = TOTAL_STYLE;

        const cols = [{wch: 5}, {wch: 37}, {wch: 25}, {wch: 9}, {wch: 110}, {wch: 20}, {wch: 14}, {wch: 18}, {wch: 10.5}, {wch: 11}];
        for (let i = 0; i < maxSlots; i++) {
          cols.push({wch: 9}, {wch: 35}, {wch: 9});
        }
        ws['!cols'] = cols;

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
        const out = XLSX.write(wb, {type: 'array', bookType: 'xlsx'});
        saveAs(new Blob([out], {type: 'application/octet-stream'}), `Venta en tienda en línea SBG - ${MONTH_NAMES[this.month]} ${this.year}.xlsx`);
      }
    }
  }

</script>
